import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  ApproveMcpServerInstructionsBodySchema,
  ApproveMcpServerToolBodySchema,
  ApproveMcpServerToolsBodySchema,
  ApproveMcpServerToolsResultSchema,
  AttachMcpToolBodySchema,
  CompleteMcpServerOAuthBodySchema,
  CompleteMcpServerOAuthResponseSchema,
  McpAttachedSkillSchema,
  McpBrokenToolPinSchema,
  McpServerDetailSchema,
  McpServerSummarySchema,
  McpServerToolViewSchema,
  RegisterMcpServerBodySchema,
  StartMcpServerOAuthBodySchema
} from "./schemas";

/**
 * The management surface for an organization's own MCP servers.
 *
 * Every row it reaches is PRIVATE to one organization, and the id in each path is
 * an `AgentExternalTool.id` — the server row's primary key IS that id, which is
 * what lets the existing tool-level tenancy and access-card machinery apply
 * unchanged rather than being reimplemented for MCP.
 */
export const ZMcpServers = {
  List: {
    method: "GET" as const,
    path: "/api/mcp-servers",
    Response: ApiSuccessSchema(z.object({ servers: z.array(McpServerSummarySchema) }))
  },

  /**
   * Every agent skill in the organization whose pinned MCP tool will be refused
   * at call time — the surface behind *a removed tool shows as broken on every
   * agent that had it attached*.
   *
   * ## Organization-wide, not per server
   *
   * An operator asking "what did that sync break" is asking about agents, and an
   * agent's skills can pin tools of several servers. Scoped to one server this
   * would have to be called once per server to answer the question anyone
   * actually has.
   *
   * ## The path is TWO segments, and that is what keeps it off `Get`
   *
   * `/api/mcp-servers/:id` is registered above this one and an Express route
   * parameter never spans a `/`, so `tool-pins/broken` cannot be captured as an
   * `:id` whatever the declaration order is. A one-segment spelling —
   * `/api/mcp-servers/broken-tool-pins` — would be captured by `:id`, and because
   * that PathVars schema demands a uuid the symptom would be a 400 that names a
   * malformed id rather than a route that does not exist.
   */
  ListBrokenToolPins: {
    method: "GET" as const,
    path: "/api/mcp-servers/tool-pins/broken",
    Response: ApiSuccessSchema(z.object({ pins: z.array(McpBrokenToolPinSchema) }))
  },

  Get: {
    method: "GET" as const,
    path: "/api/mcp-servers/:id",
    // UUID at the seam: the underlying column is @db.Uuid, so anything else
    // reaches Prisma as a validation error and surfaces as a 500.
    PathVars: z.object({ id: z.string().uuid() }),
    Response: ApiSuccessSchema(McpServerDetailSchema)
  },

  Register: {
    method: "POST" as const,
    path: "/api/mcp-servers",
    Body: RegisterMcpServerBodySchema,
    /**
     * The detail view, so the caller can render the server it just made without a
     * second round trip — with no tools in it, because nothing has been
     * discovered yet. Registration deliberately does NOT dial the server: a
     * remote that hangs would hold the request open for the whole bound ladder,
     * and a server that is briefly down is not a reason to refuse to record it.
     */
    Response: ApiSuccessSchema(McpServerDetailSchema)
  },

  Delete: {
    method: "DELETE" as const,
    path: "/api/mcp-servers/:id",
    PathVars: z.object({ id: z.string().uuid() }),
    Response: ApiSuccessSchema(z.object({ deleted: z.boolean() }))
  },

  Sync: {
    method: "POST" as const,
    path: "/api/mcp-servers/:id/sync",
    PathVars: z.object({ id: z.string().uuid() }),
    /**
     * The detail view as the sync left it — including a FAILED outcome and its
     * code. A sync that could not reach the server is a 200 describing a failed
     * sync, not a 502: the request to US succeeded, the state it recorded is
     * exactly what the caller asked to see, and an HTTP error would lose the
     * error code in favour of a status that cannot carry it.
     */
    Response: ApiSuccessSchema(McpServerDetailSchema)
  },

  /**
   * Starts an OAuth authorization for an OAUTH-mode server.
   *
   * The caller sends the user agent to `authorizationUrl`. The authorization
   * server redirects back to the BACKEND callback, which forwards the response to
   * the frontend and completes nothing; the frontend then posts it to
   * `CompleteOAuth` under the starter's own sign-in — so nothing here is polled.
   * `handshakeId` identifies the attempt for a surface that wants to follow it.
   *
   * Starting again for a server that is already connected, or whose grant was
   * refused, re-authorizes the SAME credential rather than minting a second one.
   *
   * ## The body is optional, and almost every caller sends none
   *
   * `preRegisteredClient` is the first rung of the client ladder, for an
   * authorization server that offers neither a `registration_endpoint` nor
   * client-id metadata document support and so accepts no automatic client. The
   * client is established for the ISSUER discovery resolves, not for this server,
   * and is reused by every later flow against that issuer.
   *
   * Supplying one when that issuer already has a client is REFUSED with
   * `MCP_OAUTH_CLIENT_ALREADY_ESTABLISHED` rather than merged or dropped — see
   * `PreRegisteredOAuthClientSchema`, which carries why replacing it would orphan
   * every token already issued.
   */
  StartOAuth: {
    method: "POST" as const,
    path: "/api/mcp-servers/:id/oauth/start",
    PathVars: z.object({ id: z.string().uuid() }),
    Body: StartMcpServerOAuthBodySchema,
    Response: ApiSuccessSchema(
      z.object({
        authorizationUrl: z.string().url(),
        handshakeId: z.string().uuid(),
        expiresAt: z.coerce.date()
      })
    )
  },

  /**
   * Completes an MCP server OAuth flow the SIGNED-IN caller started, from the
   * authorization response the backend callback forwarded to their browser.
   *
   * A flow another user started — in this organization or any other — is refused
   * `MCP_OAUTH_FLOW_NOT_FOUND`, exactly as one that does not exist. Every refusal is
   * a 4xx carrying one of the `MCP_OAUTH_ERROR_CODES`.
   */
  CompleteOAuth: {
    method: "POST" as const,
    path: "/api/mcp-oauth/complete",
    Body: CompleteMcpServerOAuthBodySchema,
    Response: ApiSuccessSchema(CompleteMcpServerOAuthResponseSchema)
  },

  ApproveTool: {
    method: "POST" as const,
    path: "/api/mcp-servers/:id/tools/:toolId/approve",
    PathVars: z.object({ id: z.string().uuid(), toolId: z.string().uuid() }),
    Body: ApproveMcpServerToolBodySchema,
    Response: ApiSuccessSchema(McpServerToolViewSchema)
  },

  /**
   * Approves SEVERAL of one server's tools in one call — the surface behind
   * *approve everything this server advertises*, which is eleven clicks for a
   * server like Circleback when done one at a time.
   *
   * ## The body carries a hash PER TOOL, and no shorter shape is possible
   *
   * Each pair names the contract the administrator actually read, exactly as
   * `ApproveTool` does, and the guard is evaluated per pair. An endpoint taking
   * only ids would have to read each tool's CURRENT hash and approve against
   * that — which approves, on the administrator's behalf, whatever the remote
   * server happens to be advertising at that instant. That is the
   * tool-poisoning window the approval step exists to close, so "just send the
   * ids" is not a simplification of this contract; it is its deletion.
   *
   * ## Outcomes are PER PAIR, never one verdict for the batch
   *
   * With eleven tools, one may have drifted since the screen was drawn. Refusing
   * the whole batch would lose ten sound approvals to one unrelated drift and
   * leave the administrator unable to tell which; a single boolean is the same
   * answer wearing a smaller type. So every requested pair gets its own entry,
   * and a refusal names its tool and its reason — enough to redraw the screen
   * without a second request.
   *
   * ## The path is THREE segments, which is what keeps it off `ApproveTool`
   *
   * `:id/tools/approve` cannot be captured by `:id/tools/:toolId/approve`, which
   * needs four. The `bulk/approve` spelling used elsewhere in this repo WOULD be
   * captured — `bulk` matching `:toolId` — leaving the two routes separated only
   * by declaration order. Segment count is a structural guarantee; declaration
   * order is a convention someone re-orders.
   */
  BulkApproveTools: {
    method: "POST" as const,
    path: "/api/mcp-servers/:id/tools/approve",
    PathVars: z.object({ id: z.string().uuid() }),
    Body: ApproveMcpServerToolsBodySchema,
    Response: ApiSuccessSchema(ApproveMcpServerToolsResultSchema)
  },

  /**
   * Gives one approved tool to one agent — the only act that writes an
   * `AgentToolConfigMcpToolPin`.
   *
   * ## It sits under the SERVER's path rather than the agent's
   *
   * The agent surface already has `POST /api/agents/:agentId/tools`, and the
   * tempting reading is that this belongs there. It does not: the invariants
   * being enforced are the MCP province's — the tool must be APPROVED, the pin
   * must record the contract, the schema must be one a provider can be given —
   * and the generic door knows none of them. Routed through it, an MCP skill
   * could be created with any action, no pin and an unrepresentable schema, and
   * every refusal would have to be re-derived there from the outside.
   *
   * A skill attached here is an ordinary `AgentToolConfig`, so it is READ,
   * updated and deleted through the agent surface exactly like any other. Only
   * the birth is special, because only the birth has something to prove.
   */
  AttachTool: {
    method: "POST" as const,
    path: "/api/mcp-servers/:id/tools/:toolId/attach",
    PathVars: z.object({ id: z.string().uuid(), toolId: z.string().uuid() }),
    Body: AttachMcpToolBodySchema,
    Response: ApiSuccessSchema(McpAttachedSkillSchema)
  },

  RejectTool: {
    method: "POST" as const,
    path: "/api/mcp-servers/:id/tools/:toolId/reject",
    PathVars: z.object({ id: z.string().uuid(), toolId: z.string().uuid() }),
    Response: ApiSuccessSchema(McpServerToolViewSchema)
  },

  ApproveInstructions: {
    method: "POST" as const,
    path: "/api/mcp-servers/:id/instructions/approve",
    PathVars: z.object({ id: z.string().uuid() }),
    Body: ApproveMcpServerInstructionsBodySchema,
    Response: ApiSuccessSchema(McpServerDetailSchema)
  }
} as const;
